import "./CompanyNewsIndicatorComponent.scss";
import template from "./CompanyNewsIndicatorComponent.html";
import {IComponentOptions} from "angular";
import {BehaviorSubject, Observable} from "rxjs";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {FeatureNames} from "../../../types/UserConfig";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {StringKeyed} from "../../../types/GenericTypes";
import {encodeSecurity, securityEquality} from "../../../instruments/types/CodeAndExchange";
import {HeadlineType, NewsHeadlineIndicatorModel} from "../../models/NewsHeadlineIndicatorModel";

export const NewsIndicator = "tfCompanyNewsIndicator";

export function newsIndicator(): IComponentOptions {
    return {
        template,
        bindings: {
            model: "<"
        },
        controllerAs: "ctrl",
        controller: CompanyNewsIndicatorComponentController
    };
}

export class CompanyNewsIndicatorComponentController {

    /** @input */
    public set model(model: NewsHeadlineIndicatorModel | null) {
        this.model$.next(model);
    }

    private readonly model$ = new BehaviorSubject<NewsHeadlineIndicatorModel | null>(null);

    public readonly params$: Observable<StringKeyed>;
    public readonly visible$: Observable<boolean>;
    public readonly isMarketSensitive$: Observable<boolean>;

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService) {
        this.params$ = this.buildParams();
        this.visible$ = this.buildVisible();
        this.isMarketSensitive$ = this.buildIsMarketSensitive();
    }

    private buildParams(): Observable<StringKeyed> {
        return this.model$
                   .filter(isNotNull)
                   .distinctUntilChanged(securityEquality())
                   .map(encodeSecurity)
                   .map((security) => ({security, view: "news"}))
                   .publishReplay(1)
                   .refCount();
    }

    private buildVisible(): Observable<boolean> {
        const hasHeadline$ = this.buildHasHeadline();
        const hasFeature$ = this.buildHasFeatures();

        const source$ = Observable.combineLatest(hasHeadline$, hasFeature$);

        return source$.auditTime(100)
                      .map(([hasHeadline, hasFeatures]) => hasHeadline && hasFeatures)
                      .publishReplay(1)
                      .refCount();
    }

    private buildHasHeadline() {
        return this.model$
                   .map((model) => (model != null) && (model.type !== HeadlineType.NONE))
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    private buildIsMarketSensitive(): Observable<boolean> {
        return this.model$
                   .map((h) => (h != null) && (h.type === HeadlineType.MARKET_SENSITIVE))
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    private buildHasFeatures(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .map((u) => u.hasAllFeatures(FeatureNames.SCREEN_COMPANY_INFO, FeatureNames.NEWS))
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }
}
